// ABOUTME: Impression and click events for sponsored links, sent to Firebase
// ABOUTME: Analytics. Loads Firebase lazily so callers stay test-light.

import type { AdRecord } from "../utils/ads";

export type AdEventName = "ad_impression" | "ad_click";

/** Called for every ad event, in addition to Analytics. Tests and debugging hook in here. */
export type AdEventListener = (
  event: AdEventName,
  ad: Pick<AdRecord, "id" | "title" | "url" | "sponsor">
) => void;

const listeners = new Set<AdEventListener>();

export function onAdEvent(listener: AdEventListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function send(
  event: AdEventName,
  ad: AdRecord | Pick<AdRecord, "id" | "title" | "url" | "sponsor">
) {
  const payload = { id: ad.id, title: ad.title, url: ad.url, sponsor: ad.sponsor };
  for (const l of listeners) {
    try {
      l(event, payload);
    } catch (e) {
      console.warn("Ad event listener threw:", e);
    }
  }
  // Analytics is initialized when the browser is idle and may be absent
  // (unsupported browser, blocked script). Neither is a reason to fail.
  Promise.all([import("./firebase"), import("firebase/analytics")])
    .then(([{ analytics }, { logEvent }]) => {
      if (!analytics) return;
      logEvent(analytics, event, {
        ad_id: ad.id,
        ad_title: ad.title,
        ad_sponsor: ad.sponsor,
        ad_url: ad.url,
      });
    })
    .catch(() => {
      /* analytics is best-effort */
    });
}

/** The ad was rendered on a dashboard. Call once per ad per page view. */
export function trackAdImpression(
  ad: AdRecord | Pick<AdRecord, "id" | "title" | "url" | "sponsor">
): void {
  send("ad_impression", ad);
}

/** The user activated the ad's link. */
export function trackAdClick(
  ad: AdRecord | Pick<AdRecord, "id" | "title" | "url" | "sponsor">
): void {
  send("ad_click", ad);
}
